import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { appRoutes } from '@tekman/onmat/constants';
import {
  Activity,
  ActivityTypologyName,
  CheckboxItemNode,
  EvaluationPage,
  Feature,
  FeatureName,
  GroupEvaluationStatus,
  Rubric,
  StudentsGroup,
  StudentSubcompetencesEvaluationStatus,
  SubCompetencesAssessmentStudents,
  Tags,
} from '@tekman/onmat/models';
import { Category, CategoryMapperService, RubricsService } from '@tekman/onmat/service/onmat';
import * as groupsActions from '@tekman/onmat/store/group';
import { GroupsFacade } from '@tekman/onmat/store/group';
import { AlertService, StorageService } from '@tekman/shared/service';
import { combineLatest, concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs/operators';
import { ActivitiesTeacherService } from '../service/activities-teacher.service';
import * as activitiesActions from './activities.actions';
import * as stepsActions from '@tekman/onmat/store/steps';
import { CloneUtility } from '@tekman/shared/utils';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

@Injectable()
export class ActivitiesEffects {
  constructor(
    private readonly _router: Router,
    private readonly _actions$: Actions,
    private readonly _store: Store,
    private readonly groupsFacade: GroupsFacade,
    private readonly activitiesService: ActivitiesTeacherService,
    private readonly storageService: StorageService,
    private categoryMapper: CategoryMapperService,
    private alertService: AlertService,
    private rubricsService: RubricsService,
    private _translate: TranslateService
  ) {}

  activity$ = createEffect(() =>
    combineLatest([this._actions$.pipe(ofType(activitiesActions.teacherRouterSuccess)), this.groupsFacade.selectedGroup$]).pipe(
      map(action => {
        return { activityId: action[0]?.id, selectedGroupId: action[1]?.id ?? this.storageService.selectedGroupId };
      }),
      switchMap(_response => {
        return this.activitiesService.getActivity(_response.activityId, _response.selectedGroupId).pipe(
          map((_activity: Activity) => {
            const activity = {
              ..._activity,
              categoryId: this.categoryMapper.getCategory(_activity.typology.styleId),
            };
            return activitiesActions.loadTeacherActivitySuccess({ activity });
          }),
          catchError((error: HttpErrorResponse) => {
            this._router.navigate([`${appRoutes.errorNotFound}`]);
            return of(
              activitiesActions.loadTeacherActivityFailure({
                error: {
                  message: error.message,
                  type: 'activity',
                },
              })
            );
          })
        );
      })
    )
  );

  rubrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(activitiesActions.loadTeacherActivitySuccess),
      switchMap(action => {
        return this.activitiesService.getRubrics(action.activity.id).pipe(
          map((rubrics: Rubric[]) => {
            return activitiesActions.loadTeacherActivityRubricsSuccess({ rubrics });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              activitiesActions.loadTeacherActivityRubricsFailure({
                error: { message: error.message, type: 'rubrics' },
              })
            );
          })
        );
      })
    )
  );

  steps$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.loadTeacherActivitySuccess)).pipe(
      mergeMap(action => {
        return this.activitiesService.getSteps(action.activity.step.id, this.storageService.selectedGroupId).pipe(
          map(steps => {
            return activitiesActions.loadTeacherStepsSuccess({ steps });
          }),
          catchError((error: HttpErrorResponse) =>
            of(activitiesActions.loadTeacherStepsFailure({ error: { message: error.message, type: 'steps' } }))
          )
        );
      })
    )
  );

  tags$ = createEffect(() =>
    combineLatest([this._actions$.pipe(ofType(activitiesActions.loadTeacherActivitySuccess)), this.groupsFacade.students$]).pipe(
      map(action => ({ activity: action[0].activity, students: action[1] })),
      switchMap(_response => {
        return this.activitiesService.getTags(_response.activity.id, this.storageService.selectedGroupId).pipe(
          switchMap(tags => {
            const students: StudentsGroup[] = _response.students;
            let features: Feature[] = [];
            tags = tags.map(tag => {
              if (tag.customizedStudents?.length) {
                const checkboxTree: CheckboxItemNode[] = [
                  {
                    name: this._translate.instant('f.side-nav.selectAll'),
                    children: [],
                  },
                ];
                students.forEach(student => {
                  tag.customizedStudents?.forEach(itemCheckbox => {
                    if (itemCheckbox.id === student.id) {
                      checkboxTree[0].children.push({
                        id: itemCheckbox.id,
                        name: student.lastName.trim() + student.name.trim(),
                        checked: itemCheckbox.value,
                      });
                    }
                  });
                  const amountStudents = tag.customizedStudents?.length;
                  const amountChecked = tag.customizedStudents?.filter(checkedStudent => checkedStudent.value).length;
                  checkboxTree[0].checked = amountStudents === amountChecked;
                  tag = { ...tag, customize: checkboxTree };
                });
              }
              return tag;
            });
            features = this.mapTagsFeaturesNav(tags);
            return of(
              activitiesActions.loadTeacherTagsSuccess({ tags }),
              activitiesActions.addTeacherFeatures({ features }),
              activitiesActions.loadEvaluationPages({ activity: _response.activity, features })
            );
          }),
          catchError((error: HttpErrorResponse) => {
            return of(activitiesActions.loadTeacherTagsFailure({ error: { message: error?.message, type: 'tags' } }));
          })
        );
      })
    )
  );

  teachingInfo$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.loadTeacherActivitySuccess)).pipe(
      switchMap(action => {
        return this.activitiesService.getTeachingInfo(action.activity.id).pipe(
          map(teachingInfo => {
            return activitiesActions.loadTeachingInfoSuccess({
              teachingInfo,
            });
          }),
          catchError(error => {
            return of(activitiesActions.loadTeachingInfoFailure({ error: { message: error.message } }));
          })
        );
      })
    )
  );

  averageGrades$ = createEffect(() =>
    this._actions$.pipe(
      ofType(activitiesActions.loadTeacherActivitySuccess),
      switchMap(action => {
        if (
          action.activity?.categoryId === Category.EJERCICIOS &&
          action.activity?.typology?.name !== ActivityTypologyName.DOWNLOADABLE_EXERCISE
        ) {
          return this.activitiesService.getAverageGrades(action.activity.id, this.storageService.selectedGroupId).pipe(
            map(averageGrades => {
              return activitiesActions.saveAverageGradesSuccess({ averageGrades });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                activitiesActions.saveAverageGradesFailure({
                  error: { message: error.message, type: 'averageGrades' },
                })
              );
            })
          );
        } else {
          return new Observable<Action>();
        }
      })
    )
  );

  /* loadEvaluationPages$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.loadEvaluationPages)).pipe(
      mergeMap(action => {
        const activityExercises = [...action.activity.exercises];
        const exercises: Exercise[] = [];
        activityExercises.forEach(exercise => {
          exercises.push({ ...exercise, isEvaluation: false });
        });
        return of(
          activitiesActions.loadEvaluationPagesSuccess({
            exercises,
          })
        );
      })
    )
  );*/

  saveFeature$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.saveFeature)).pipe(
      mergeMap(action => {
        this.activitiesService.isLoadingCursorSubject.next(true);
        return this.activitiesService
          .saveFeature(action.feature, action.activity.id, this.storageService.selectedGroupId, action.studentId)
          .pipe(
            switchMap(() => {
              let sessions;
              this._store
                .select(state => state)
                .pipe(first())
                .subscribe(data => {
                  const _data = CloneUtility.deepClone(data);
                  if (!_data['activities']['steps']?.sessions) return;
                  sessions = Object.keys(_data['activities']['steps']?.sessions)?.map(key => _data['steps']?.sessions[key])[0];
                  const activities = sessions?.map(s => s.items.map(i => i.activity));
                  const activity = activities?.flat().find(a => a.id === action.activity.id);
                  activity[action.feature.name] = action.feature.value;
                });
              this.activitiesService.isLoadingCursorSubject.next(false);
              const feature = action.features.map(feature => {
                return {
                  ...feature,
                  value: feature.name === action.feature.name ? action.feature.value : feature.value,
                };
              });
              return of(
                activitiesActions.saveFeatureSuccess({
                  features: feature,
                }),
                // isHidden value is the only one we need to change
                activitiesActions.modifyFeatureSuccess({
                  activityId: action.activity.id,
                  featureName: 'isHidden',
                  value: feature[0].value,
                }),
                stepsActions.loadSessionsSuccess({
                  stepId: action.activity.step.id,
                  sessions: sessions,
                  groupId: action.selectedGroupId,
                })
              );
            }),
            catchError((error: HttpErrorResponse) => {
              this.activitiesService.isLoadingCursorSubject.next(false);
              return of(activitiesActions.saveFeatureFailure({ error: { message: error?.message, type: 'saveFeature' } }));
            })
          );
      })
    )
  );

  subcompetences$ = createEffect(() =>
    combineLatest([this._actions$.pipe(ofType(activitiesActions.loadSubcompetencesAssessmentStudents)), this.groupsFacade.students$]).pipe(
      mergeMap(action => {
        return concat(
          forkJoin([
            this.activitiesService.getStudentsGroupSubcompetence(action[0].activity, this.storageService.selectedGroupId),
            this.activitiesService.getStudentsActivityStatus(action[0].activity, this.storageService.selectedGroupId),
          ])
        ).pipe(
          switchMap(response => {
            //const teams: CheckboxItemNode[] = action[0].teams;
            const evaluationSubcompetence = response[0].map(evaluation => {
              evaluation.subCompetenceGrade = evaluation.subCompetenceGrade.map(grades => {
                return {
                  ...grades,
                  grade:
                    grades.grade || grades.grade === 0
                      ? parseFloat(String(grades.grade)).toLocaleString(this.getLocale(), { minimumFractionDigits: 2 })
                      : '-',
                };
              });
              return {
                ...evaluation,
                subCompetenceGrade: evaluation.subCompetenceGrade,
              };
            });
            const checkboxTree: CheckboxItemNode[] = [
              {
                name: this._translate.instant('f.side-nav.selectAll'),
                children: [],
              },
            ];
            /*teams[0].children.forEach(team => {
              const studentTree: CheckboxItemNode[] = [];
              if (team.children) {
                team.children.forEach(student => {
                  evaluationSubcompetence.forEach(evaluation => {
                    if (student.id === evaluation.studentId) {
                      studentTree.push({
                        ...student,
                        columns: evaluation.subCompetenceGrade,
                      });
                    }
                  });
                  if (evaluationSubcompetence.length === 0) {
                    studentTree.push(student);
                  }
                  team = { ...team, children: studentTree };
                });
              } else {
                evaluationSubcompetence.forEach(evaluation => {
                  if (team.id === evaluation.studentId) {
                    team = { ...team, columns: evaluation.subCompetenceGrade };
                  }
                });
              }
              checkboxTree[0].children.push({ ...team });
            });*/
            const studentsSubcompetencesStatus = response[0].map((studentSubcompetence, index) =>
              Object.assign({}, studentSubcompetence, response[1][index])
            );
            const students = action[1].map(student => {
              const status = response[1].find(studentStatus => student.id === studentStatus.studentId);
              if (response[0].length > 0) {
                const subcompetences = response[0].find(studentSubcompetences => student.id === studentSubcompetences.studentId);
                const grades = subcompetences.subCompetenceGrade.map(subcompetence => {
                  return subcompetence.grade !== '-'
                    ? subcompetence.grade.toLocaleString(this.getLocale(), {
                        minimumFractionDigits: 2,
                      })
                    : subcompetence.grade.toString();
                });
                return {
                  id: student.id,
                  name: `${student.lastName.trim()}, ${student.name.trim()}`,
                  activityStatus: status.state,
                  grade1: grades[0],
                  grade2: grades.length > 1 ? grades[1] || null : null,
                  isSent: status.isSent,
                };
              } else {
                return {
                  id: student.id,
                  name: `${student.lastName.trim()} ${student.name.trim()}`,
                  activityStatus: status.state,
                  isSent: status.isSent,
                };
              }
            });
            const studentsStatus = students
              .map(student => {
                let studentItem: StudentSubcompetencesEvaluationStatus;
                if (studentsSubcompetencesStatus.length > 0) {
                  studentsSubcompetencesStatus.forEach(studentSubcompetence => {
                    if (student.id === studentSubcompetence.studentId) {
                      studentItem = {
                        ...studentSubcompetence,
                        id: student.id,
                        name: student.name,
                        activityStatus: student.activityStatus,
                        grade1: student.grade1,
                        grade2: student.grade2,
                        isSent: studentSubcompetence.isSent,
                      };
                    }
                  });
                } else {
                  studentItem = {
                    id: student.id,
                    studentId: student.id,
                    name: student.name,
                    activityStatus: student.activityStatus,
                    state: student.activityStatus,
                    isSent: student.isSent,
                  };
                }
                return studentItem;
              })
              .sort((a, b) => {
                if (a.name.toLowerCase() !== b.name.toLowerCase()) {
                  return a.name.localeCompare(b.name);
                }
              });
            const groupEvaluationStatus: GroupEvaluationStatus = {
              studentsStatus: studentsStatus,
              countDelivered: studentsStatus.filter(student => student.isSent).length,
              countPending: studentsStatus.filter(student => !student.isSent).length,
            };
            return of(
              activitiesActions.loadEvaluationStudentSubcompetenceStatusSuccess({
                groupEvaluationSubcompetenceStatus: groupEvaluationStatus,
              }),
              groupsActions.updatedTeamsSuccess({ teams: checkboxTree })
            );
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              activitiesActions.loadEvaluationStudentSubcompetenceStatusFailure({
                error: { message: error.message, type: 'subcompetences' },
              })
            );
          })
        );
      })
    )
  );

  applySubcompetenceAssessmentStudents$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.applySubcompetencesAssessmentStudents)).pipe(
      switchMap(action => {
        return this.activitiesService
          .applySubcompetenceAssessmentStudents(
            action.activity.id,
            this.storageService.selectedGroupId,
            action.subcompetencesAssessmentStudents
          )
          .pipe(
            switchMap(() => {
              const teams: CheckboxItemNode[] = action.teams;
              const subcompetencesAssessmentStudents: SubCompetencesAssessmentStudents = action.subcompetencesAssessmentStudents;
              const checkboxTree: CheckboxItemNode[] = [
                {
                  name: this._translate.instant('f.side-nav.selectAll'),
                  children: [],
                },
              ];
              teams[0].children.forEach(team => {
                if (team.children) {
                  const studentsTeam = team.children.map(student => {
                    subcompetencesAssessmentStudents.students.forEach(assestmentStudent => {
                      if (student.id === assestmentStudent) {
                        const grades = student.columns.map(column => {
                          if (column.subCompetenceId === subcompetencesAssessmentStudents.subCompetenceId) {
                            column = {
                              ...column,
                              grade: subcompetencesAssessmentStudents.grade,
                            };
                          }
                          return column;
                        });
                        student = { ...student, columns: grades };
                      }
                    });
                    return student;
                  });
                  checkboxTree[0].children.push({
                    ...team,
                    children: studentsTeam,
                  });
                } else {
                  subcompetencesAssessmentStudents.students.forEach(assestmentStudent => {
                    if (team.id === assestmentStudent) {
                      const grades = team.columns.map(column => {
                        if (column.subCompetenceId === subcompetencesAssessmentStudents.subCompetenceId) {
                          column = {
                            ...column,
                            grade: subcompetencesAssessmentStudents.grade,
                          };
                        }
                        return column;
                      });
                      team = { ...team, columns: grades };
                    }
                  });
                  checkboxTree[0].children.push(team);
                }
              });
              this.alertService.openAlert(this._translate.instant('f.evaluation.alert.success'));
              return of(
                groupsActions.loadTeamsSuccess({
                  teams: checkboxTree,
                  students: action.studentsGroup,
                  groupId: this.storageService.selectedGroupId,
                }),
                activitiesActions.loadSubcompetencesAssessmentStudents({
                  activity: action.activity.id,
                  teams: checkboxTree,
                })
              );
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                activitiesActions.applySubcompetencesAssessmentStudentsFailure({
                  error: { message: error.message, type: 'applySubcompetences' },
                })
              )
            )
          );
      })
    )
  );

  studentActivityAnswersEvaluation$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.loadActivityStudentAnswers)).pipe(
      switchMap(action => {
        this.activitiesService.isLoadingAnswersStudentSubject.next(true);
        return forkJoin([
          this.activitiesService.getStudentAnswers(action.activity.id, this.storageService.selectedGroupId, action.studentId),
          this.activitiesService
            .getStudentAnswersAverage(action.activity.id, this.storageService.selectedGroupId, action.studentId)
            .pipe(catchError(error => of(error))),
        ]).pipe(
          map(([answers, averages]) => {
            this.activitiesService.isLoadingAnswersStudentSubject.next(false);
            return activitiesActions.loadActivityStudentAnswersSuccess({
              activity: action.activity,
              answers,
              averages,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.activitiesService.isLoadingAnswersStudentSubject.next(false);
            return of(
              activitiesActions.loadActivityStudentAnswersFailure({
                error: { message: error.message, type: 'loadActivityAnswers' },
              })
            );
          })
        );
      })
    )
  );

  repeatActivity$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.repeatActivity)).pipe(
      mergeMap(action => {
        return this.activitiesService.repeatActivity(action.activity.id, this.storageService.selectedGroupId, action.selectedStudent).pipe(
          map(status => {
            this.activitiesService.isLoadingCursorSubject.next(false);
            this.alertService.openAlert(this._translate.instant('f.alert.repeatActivity'));
            return activitiesActions.repeatActivitySuccess({
              status: status.state,
              selectedStudent: action.selectedStudent,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.activitiesService.isLoadingCursorSubject.next(false);
            this.alertService.openAlert(this._translate.instant('f.alert.error.repeatActivity'), false);
            return of(activitiesActions.repeatActivityFailure({ error: { message: error.message } }));
          })
        );
      }),
      catchError((error: HttpErrorResponse) => {
        this.activitiesService.isLoadingCursorSubject.next(false);
        this.alertService.openAlert(this._translate.instant('f.alert.error.repeatActivity'), false);
        return of(activitiesActions.repeatActivityFailure({ error: { message: error.message, type: 'repeatActivity' } }));
      })
    )
  );

  teacherGuide$ = createEffect(() =>
    this._actions$.pipe(
      ofType(activitiesActions.loadTeacherGuide),
      switchMap(action => {
        return this.activitiesService.getTeacherGuide(action.activity.id, action.exerciseId).pipe(
          map(response => {
            return activitiesActions.loadTeacherGuideSuccess({
              exerciseId: action.exerciseId,
              teacherGuide:
                response.teacherGuide !== '' ? response.teacherGuide : this._translate.instant('f.concealable.guide.empty.label'),
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              activitiesActions.loadTeacherGuideFailure({
                error: { message: error.message, type: 'teacherGuide' },
              })
            );
          })
        );
      })
    )
  );

  teacherSolutionary$ = createEffect(() =>
    this._actions$.pipe(
      ofType(activitiesActions.loadTeacherSolutionary),
      switchMap(action => {
        return this.activitiesService.getTeacherSolutionary(action.activity.id, action.exerciseId).pipe(
          map(response => {
            return activitiesActions.loadSolutionarySuccess({
              exerciseId: action.exerciseId,
              solutionary:
                response.solutionary !== '' ? response.solutionary : this._translate.instant('f.concealable.solutionary.empty.label'),
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              activitiesActions.loadSolutionaryFailure({
                error: { message: error.message, type: 'teacherSolutionary' },
              })
            );
          })
        );
      })
    )
  );

  saveEvaluationPages$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.saveEvaluationPages)).pipe(
      switchMap(action => {
        const evaluationPage: EvaluationPage = {
          exerciseId: action.exercise.id,
          isEvaluable: action.exercise.isHidden,
        };
        return this.activitiesService.saveEvaluationPages(action.activity.id, this.storageService.selectedGroupId, evaluationPage).pipe(
          map(() => {
            return activitiesActions.saveEvaluationPagesSuccess({
              evaluationPage,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              activitiesActions.saveEvaluationPagesFailure({
                error: { message: error.message, type: 'saveEvaluationPages' },
              })
            )
          )
        );
      })
    )
  );

  teacherStudentMultiAnswer$ = createEffect(() =>
    this._actions$.pipe(ofType(activitiesActions.loadStudentMultianswersSuccess)).pipe(
      mergeMap(action => {
        return this.rubricsService.getAnswersMultiResponse(action.activityId, this.storageService.selectedGroupId, action.studentId).pipe(
          map(multiAnswers => {
            return activitiesActions.loadStudentAnswersAPracticarSuccess({
              rubricsMultiChoice: multiAnswers,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              activitiesActions.loadStudentAnswersAPracticarFailure({
                error: { message: error.message, type: 'multianswer' },
              })
            )
          )
        );
      })
    )
  );

  materials$ = createEffect(() =>
    this._actions$.pipe(
      ofType(activitiesActions.loadTeacherActivitySuccess),
      switchMap(action => {
        return this.activitiesService.getMaterials(action.activity.id, this.storageService.selectedGroupId).pipe(
          map(materials => {
            return activitiesActions.loadActivityMaterialsSuccess({
              materials,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(activitiesActions.loadActivityMaterialsFailure({ error: { message: error.message } }))
          )
        );
      })
    )
  );

  private mapTagsFeaturesNav(tags: Tags[]): Feature[] {
    const mapFeatures: Feature[] = [
      {
        name: FeatureName.IS_SOLUTIONARY,
        title: marker('f.characteristics.solution'),
        visible: false,
        active: false,
        disable: false,
        help: marker('f.side-nav.characteristics.solution.help'),
        customize: [],
        amountCustom: 0,
      },
      {
        name: FeatureName.IS_HIDDEN,
        title: marker('f.characteristics.hidden'),
        visible: false,
        active: false,
        disable: false,
        help: marker('f.side-nav.characteristics.hidden.help'),
        customize: [],
      },
      {
        name: FeatureName.IS_SELFCORRECTING,
        title: marker('f.characteristics.selfcorrecting'),
        visible: false,
        active: false,
        disable: false,
        help: marker('f.side-nav.characteristics.selfcorrecting.help'),
        customize: [],
      },
    ];
    return tags
      .map(element => {
        let feature: Feature;
        if ('isEditable' in element) {
          feature = mapFeatures.find(item => item.name === element.name && element.isEditable);
        } else {
          feature = mapFeatures.find(item => item.name === element.name);
        }
        if (feature) {
          if (element.name === feature.name) {
            feature = {
              ...feature,
              active: element.value === undefined ? element.customizedStudents?.some(checked => checked.value) : element.value,
              amountCustom:
                element.customizedStudents === undefined ? null : element.customizedStudents?.filter(checked => checked.value).length,
              disable: element.customizedStudents === undefined ? false : element.customizedStudents?.length === 0,
            };
          }
          return Object.assign(feature, element);
        }
      })
      .filter(notUndefined => notUndefined !== undefined);
  }

  private getLocale = (): string => (window.location.hostname.includes('mx') ? 'es-MX' : 'es-ES');
}
